import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDSelect from "components/MDSelect"
import { useRefined } from "../ui/refined"
import {
  REGION_OPTIONS,
  CENTER_OPTIONS,
  CATEGORY_OPTIONS,
  DEPARTMENT_OPTIONS,
} from "../constants"

// Reusable filter-bar card. Controlled: the page owns the filter state (usually
// via useProcurementFilters → URL query params) and passes it in. Each page
// enables only the fields it needs through the `fields` array.
const FIELD_LABELS = {
  search: "Search",
  region: "Region",
  center: "Center",
  category: "Category",
  department: "Department",
  supplier: "Supplier",
}

const ProcurementFilterBar = ({
  fields = [],
  values = {},
  onChange,
  onReset,
  extra = null,
  statusOptions = [],
  stageOptions = [],
  urgencyOptions = [],
  categoryOptions = CATEGORY_OPTIONS,
  supplierOptions = [],
  // Caller-provided center list (e.g. from useGetProcurementCentersQuery, the
  // same source the other OR/PO/PR list pages use) — when omitted this falls
  // back to the hardcoded CENTER_OPTIONS scoped by the selected region, the
  // pre-existing behavior kept for callers (ToPayListContainer) that don't
  // pass one.
  centerOptions: centerOptionsProp,
  itemOptions = [],
  itemsLoading = false,
}) => {
  const refined = useRefined()
  const has = (f) => fields.includes(f)
  const set = (patch) => onChange && onChange(patch)

  // Centers scoped to the selected region for a cleaner choice list.
  const centerOptions =
    centerOptionsProp ||
    (values.region ? CENTER_OPTIONS.filter((c) => c.region === values.region) : CENTER_OPTIONS)

  const findOpt = (opts, val) => opts.find((o) => o.value === val) || null

  const selectedItemIds = String(values.item || "").split(",").filter(Boolean)
  const selectedItemOptions = itemOptions.filter((o) => selectedItemIds.includes(String(o.value)))

  return (
    <MDBox mb={2} p={2} bgColor="white" borderRadius="lg" shadow="sm" className={refined ? "ds-filters pc-md-filters" : undefined}>
      <Grid container spacing={2} alignItems="flex-end">
        {has("search") && (
          <Grid item xs={12} md={3}>
            <MDInput
              fullWidth
              labelType="non-sticky"
              labelText={FIELD_LABELS.search}
              placeholder="Search…"
              value={values.search || ""}
              onChange={(e) => set({ search: e.target.value })}
            />
          </Grid>
        )}

        {has("region") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText={FIELD_LABELS.region}
              placeholder="Select…"
              options={REGION_OPTIONS}
              value={findOpt(REGION_OPTIONS, values.region)}
              onChange={(o) => set({ region: o?.value || "", center: "" })}
            />
          </Grid>
        )}

        {has("center") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText={FIELD_LABELS.center}
              placeholder="Select…"
              options={centerOptions}
              value={findOpt(CENTER_OPTIONS, values.center)}
              onChange={(o) => set({ center: o?.value || "" })}
            />
          </Grid>
        )}

        {has("category") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText={FIELD_LABELS.category}
              placeholder="Select…"
              options={categoryOptions}
              value={findOpt(categoryOptions, values.category)}
              onChange={(o) => set({ category: o?.value || "" })}
            />
          </Grid>
        )}

        {has("department") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText={FIELD_LABELS.department}
              placeholder="Select…"
              options={DEPARTMENT_OPTIONS}
              value={findOpt(DEPARTMENT_OPTIONS, values.department)}
              onChange={(o) => set({ department: o?.value || "" })}
            />
          </Grid>
        )}

        {has("supplier") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText={FIELD_LABELS.supplier}
              placeholder="Select…"
              options={supplierOptions}
              value={findOpt(supplierOptions, values.supplier)}
              onChange={(o) => set({ supplier: o?.value || "" })}
            />
          </Grid>
        )}

        {has("status") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText="Status"
              placeholder="Select…"
              options={statusOptions}
              value={findOpt(statusOptions, values.status)}
              onChange={(o) => set({ status: o?.value || "" })}
            />
          </Grid>
        )}

        {has("stage") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText="Stage"
              placeholder="Select…"
              options={stageOptions}
              value={findOpt(stageOptions, values.stage)}
              onChange={(o) => set({ stage: o?.value || "" })}
            />
          </Grid>
        )}

        {has("urgency") && (
          <Grid item xs={12} md={2}>
            <MDSelect
              isClearable
              labelType="non-sticky"
              labelText="Urgency"
              placeholder="Any urgency"
              options={urgencyOptions}
              value={findOpt(urgencyOptions, values.urgency)}
              onChange={(o) => set({ urgency: o?.value || "" })}
            />
          </Grid>
        )}

        {has("period") && (
          <>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="date"
                labelType="non-sticky"
                labelText="From"
                value={values.from || ""}
                onChange={(e) => set({ from: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="date"
                labelType="non-sticky"
                labelText="To"
                value={values.to || ""}
                onChange={(e) => set({ to: e.target.value })}
              />
            </Grid>
          </>
        )}

        {has("item") && (
          <Grid item xs={12} md={3}>
            <MDSelect
              isMulti
              isClearable
              displayChips
              labelType="non-sticky"
              labelText="Item"
              placeholder="All items"
              isLoading={itemsLoading}
              options={itemOptions}
              value={selectedItemOptions}
              onChange={(opts) => set({ item: (opts || []).map((o) => o.value).join(",") })}
            />
          </Grid>
        )}

        {/* created_at range — distinct from "period" (kept for callers that
            filter by a different date field) so both can coexist. */}
        {has("createdPeriod") && (
          <>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="date"
                labelType="non-sticky"
                labelText="Created from"
                value={values.fromDate || ""}
                onChange={(e) => set({ fromDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MDInput
                fullWidth
                type="date"
                labelType="non-sticky"
                labelText="Created to"
                value={values.toDate || ""}
                onChange={(e) => set({ toDate: e.target.value })}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md="auto">
          <MDButton variant="outlined" color="secondary" onClick={onReset}>
            RESET FILTERS
          </MDButton>
        </Grid>

        {extra && (
          <Grid item xs={12} md="auto">
            {extra}
          </Grid>
        )}
      </Grid>
    </MDBox>
  )
}

export default ProcurementFilterBar
